import React, { forwardRef, useState } from 'react'
import { css } from '@emotion/react'
import get from 'lodash/get'
import { fontSize, spacing } from '@retailer-platform/shared-components'
import { useDomainMessages, type DomainMessages } from 'domains/collections/utils/domain/intl'
import { useDashIntl } from 'intl/intl.hooks'
import { type DashMessages } from 'intl/intl.types'
import { Input, type InputProps } from 'gin-and-tonic/common/input/Input'
import { type ControllerProps } from 'utils/react-hook-form/reactHookForm.types'
import { useFormErrorMessageMap } from 'utils/react-hook-form/reactHookForm.hooks'
import {
  type FormErrorMessageMap,
  type ErrorRecordType,
} from 'utils/react-hook-form/reactHookForm.types'
import { CollectionsFormInputWrapper } from './CollectionsFormInputWrapper'

export interface CollectionsFormInputProps
  extends Omit<InputProps, 'name' | 'placeholderMessageId' | 'errorMessageId'>,
    Pick<ControllerProps, 'control'> {
  errorMessage?: DomainMessages
  identifier?: string
  labelId?: DomainMessages
  showLength?: boolean
  errors?: ErrorRecordType
  errorMessages?: FormErrorMessageMap
  placeholderMessageId?: DomainMessages
  errorMessageId?: DomainMessages
}

const outerStyles = css({
  margin: '0px',
})

const inputStyles = css({
  height: 46,
  fontSize: fontSize.X15,
  '& + div:not(:empty)': {
    marginTop: spacing.X4,
    marginLeft: 0,
    marginBottom: 0,
  },
})

export const CollectionsFormInput = forwardRef<HTMLInputElement, CollectionsFormInputProps>(
  (
    {
      errors = {},
      errorMessage,
      maxLength,
      labelId,
      placeholderMessageId,
      identifier,
      onChange,
      defaultValue = '',
      showLength,
      ...rest
    },
    ref
  ) => {
    const messages = useDomainMessages({
      label: labelId,
      placeholder: placeholderMessageId || labelId,
    })

    const [value, setValue] = useState(defaultValue)

    const onTextChange = event => {
      onChange?.(event)
      setValue(event.target.value)
    }

    const intl = useDashIntl()

    const defaultErrorMessage: Record<string, DomainMessages> = {
      required: errorMessage,
    }

    const errorMessageId = useFormErrorMessageMap(defaultErrorMessage)(get(errors, identifier))
    const formattedMessage =
      errorMessageId && intl.formatMessage({ id: errorMessageId }, { label: messages.label })

    return (
      <CollectionsFormInputWrapper
        label={messages.label}
        maxLength={maxLength}
        showLength={showLength}
        value={value.toString()}
      >
        <Input
          {...rest}
          ref={ref}
          name={identifier}
          maxLength={maxLength}
          onChange={onTextChange}
          placeholder={messages.placeholder}
          styles={inputStyles}
          outerStyles={outerStyles}
          // Default value only allowed for controlled components
          defaultValue={ref ? undefined : defaultValue}
          errorMessage={formattedMessage}
          errorMessageId={
            errorMessageId
              ? ('collectionsDomain.collectionsForm.form.validation.default' as DashMessages)
              : null
          }
        />
      </CollectionsFormInputWrapper>
    )
  }
)
